import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { AppApis } from '@app/core/APIs/app-apis';
import type {
  AlertTemplateCreateRequest,
  AlertTemplateContextProfile,
  AlertTemplateDetail,
  AlertTemplateDraft,
  AlertTemplatePage,
  AlertTemplatePreview,
  AlertTemplateSelectionAction,
} from './platform-alert-templates.models';

const options = {
  withCredentials: true,
  headers: new HttpHeaders({
    'skip-error-interceptor': 'yes',
    'skip-loading-interceptor': 'yes',
  }),
};
const write = (key: string) => ({
  ...options,
  headers: options.headers.set('Idempotency-Key', key),
});
const source = (draft: AlertTemplateDraft) => ({
  expectedRevision: draft.revision,
  expectedContentHash: draft.contentHash,
});

@Injectable({ providedIn: 'root' })
export class PlatformAlertTemplatesService {
  private readonly http = inject(HttpClient);
  private readonly apis = inject(AppApis);
  private get url(): string {
    return this.apis.core.manual.getPlatformAlertTemplates();
  }
  list(page = 1, search = '') {
    return this.http.get<AlertTemplatePage>(this.url, {
      ...options,
      params: new HttpParams()
        .set('page', page)
        .set('pageSize', 20)
        .set('search', search),
    });
  }
  detail(id: number) {
    return this.http.get<AlertTemplateDetail>(`${this.url}/${id}`, options);
  }
  contextProfiles() {
    return this.http.get<AlertTemplateContextProfile[]>(
      `${this.url}/context-profiles`,
      options,
    );
  }
  create(request: AlertTemplateCreateRequest, key: string) {
    return this.http.post<AlertTemplateDetail>(this.url, request, write(key));
  }
  save(
    id: number,
    draft: AlertTemplateDraft,
    titleTemplate: string,
    bodyTemplate: string,
    key: string,
    profile?: Pick<AlertTemplateContextProfile, 'code' | 'version' | 'hash'>,
  ) {
    return this.http.put<AlertTemplateDetail>(
      `${this.url}/${id}/draft`,
      {
        expectedRevision: draft.revision,
        titleTemplate,
        bodyTemplate,
        ...(profile
          ? {
              profileCode: profile.code,
              profileVersion: profile.version,
              profileHash: profile.hash,
            }
          : {}),
      },
      write(key),
    );
  }
  preview(id: number, draft: AlertTemplateDraft) {
    return this.http.post<AlertTemplatePreview>(
      `${this.url}/${id}/preview`,
      source(draft),
      options,
    );
  }
  review(id: number, draft: AlertTemplateDraft, key: string) {
    return this.http.post<AlertTemplateDetail>(
      `${this.url}/${id}/review`,
      source(draft),
      write(key),
    );
  }
  publish(
    id: number,
    draft: AlertTemplateDraft,
    reviewId: string,
    key: string,
  ) {
    return this.http.post<AlertTemplateDetail>(
      `${this.url}/${id}/publish`,
      {
        ...source(draft),
        reviewId,
      },
      write(key),
    );
  }
  select(
    id: number,
    action: AlertTemplateSelectionAction,
    versionId: number | null,
    expectedSelectionRevision: number,
    reason: string,
    key: string,
  ) {
    return this.http.post<AlertTemplateDetail>(
      `${this.url}/${id}/${action}`,
      {
        versionId,
        expectedSelectionRevision,
        reason,
      },
      write(key),
    );
  }
  previewVersion(id: number, versionId: number) {
    return this.http.get<AlertTemplatePreview>(
      `${this.url}/${id}/versions/${versionId}/preview`,
      options,
    );
  }
}
